import {isFeatureEnabled} from '@github-ui/feature-flags'
import {SingleSignOnBanner} from '@github-ui/single-sign-on-banner'
import {RepoIcon} from '@primer/octicons-react'
import {Heading} from '@primer/react'
import {useMemo} from 'react'

import {isDocset} from '../utils/copilot-chat-helpers'
import type {CopilotChatRepo, Docset} from '../utils/copilot-chat-types'
import {useChatState} from '../utils/CopilotChatContext'
import {ChatWithCopilotHeader} from './ChatWithCopilotHeader'
import {KnowledgeBaseAvatar} from './KnowledgeBaseAvatar'
import {COPILOT_CHAT_MENU_PORTAL_ROOT} from './PortalContainerUtils'
import TopicList from './TopicList'
import styles from './TopicPicker.module.css'

export const TopicPicker = () => {
  const state = useChatState()

  const ssoOrgNames = useMemo(() => {
    if (!state.ssoOrganizations) return []
    return state.ssoOrganizations.map(org => org.login)
  }, [state.ssoOrganizations])

  return (
    <div className={styles.TopicPickerContainer}>
      <div className={styles.mainContentContainer}>
        <ChatWithCopilotHeader mode={state.mode} />
        <div className={styles.topicListContainer}>
          <TopicList />
        </div>
        {!isFeatureEnabled('global_sso_banner') && (
          <SingleSignOnBanner
            portalContainerName={COPILOT_CHAT_MENU_PORTAL_ROOT}
            protectedOrgs={ssoOrgNames}
            redirectURI={() => `/search/refresh_blackbird_caches?return_to=${location.href}`}
            className={styles.SingleSignOnBanner}
          />
        )}
        <TopicDescription currentTopic={state.currentTopic} />
      </div>
    </div>
  )
}

function TopicDescription({currentTopic}: {currentTopic: CopilotChatRepo | Docset | undefined}) {
  if (!isDocset(currentTopic)) return null

  return (
    <div className={styles.topicDescriptionContainer}>
      <DocsetDescription docset={currentTopic} />
    </div>
  )
}

function DocsetDescription({docset}: {docset: Docset}) {
  return (
    <div className={styles.docsetDescriptionCard}>
      <div className={styles.docsetHeaderContainer}>
        <KnowledgeBaseAvatar docset={docset} size={24} />
        <Heading as="h3" className={styles.Heading}>
          {docset.name}
        </Heading>{' '}
        <span className={styles.statusText}>Knowledge base added to conversation</span>
      </div>
      <Heading as="h4" className={styles.repositoriesSubheading}>
        Repositories in this knowledge base
      </Heading>
      <div className={styles.repositoriesContainer}>
        {docset.repos.map(r => (
          <div key={r} className={styles.repositoryItem}>
            <RepoIcon />
            {` ${r}`}
          </div>
        ))}
      </div>
    </div>
  )
}

try{ TopicPicker.displayName ||= 'TopicPicker' } catch {}
try{ TopicDescription.displayName ||= 'TopicDescription' } catch {}
try{ DocsetDescription.displayName ||= 'DocsetDescription' } catch {}